import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { SalePaymentSection, usePurchasePayments } from '../components/cosmetics_SalPaymentSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser, faPhone, faCalendarAlt, faFileInvoice, faBox,
    faRupeeSign, faTag, faPlus, faTrash, faSave, faTimes, faSpinner,
    faCheckCircle, faExclamationTriangle, faBoxes, faBarcode, faFileAlt,
    faToggleOn, faToggleOff, faCopy, faTrashAlt, faBuilding, faCalculator,
    faMoneyBill, faChevronDown, faSearch
} from '@fortawesome/free-solid-svg-icons';

const ACCOUNTS_ENDPOINT = 'https://ourzestapp.online/oz_backend/mechanics_module/mechanics_account.php';

// ==============================================
// GLOBAL BARCODE SCANNER HOOK - FIXED
// ==============================================
const useBarcodeScanner = (onBarcodeScanned, isActive = true) => {
    const bufferRef = useRef('');
    const scanStartedAtRef = useRef(0);
    const lastTimeRef = useRef(0);
    const scanTimerRef = useRef(null);
    const isProcessingRef = useRef(false);
    const isLikelyScanningRef = useRef(false);
    const leakSnapshotRef = useRef(null);

    useEffect(() => {
        if (!isActive) return;

        const SCAN_MAX_INTERVAL_MS = 80;
        const MAX_AVERAGE_INTERVAL_MS = 60;
        const MIN_BARCODE_LENGTH = 4;
        const BUFFER_TIMEOUT_MS = 180;

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value'
        )?.set;

        const setNativeValue = (el, value) => {
            if (!el) return;
            if (el.tagName === 'INPUT' && nativeInputValueSetter) {
                nativeInputValueSetter.call(el, value);
            } else {
                el.value = value;
            }
            el.dispatchEvent(new Event('input', { bubbles: true }));
        };

        const restoreLeak = () => {
            const snap = leakSnapshotRef.current;
            if (snap && snap.element && snap.element.isConnected) {
                setNativeValue(snap.element, snap.value);
                if (snap.selectionStart != null && snap.element.setSelectionRange) {
                    try {
                        snap.element.setSelectionRange(snap.selectionStart, snap.selectionStart);
                    } catch (e) { }
                }
            }
            leakSnapshotRef.current = null;
        };

        const resetBuffer = () => {
            bufferRef.current = '';
            scanStartedAtRef.current = 0;
            isLikelyScanningRef.current = false;
            leakSnapshotRef.current = null;
        };

        const commitBufferedScan = (event = null) => {
            clearTimeout(scanTimerRef.current);
            const candidate = bufferRef.current.trim();
            const averageInterval = candidate.length > 1 && scanStartedAtRef.current
                ? (lastTimeRef.current - scanStartedAtRef.current) / (candidate.length - 1)
                : Number.MAX_SAFE_INTEGER;
            const isConfirmedScan = isLikelyScanningRef.current &&
                candidate.length >= MIN_BARCODE_LENGTH &&
                averageInterval <= MAX_AVERAGE_INTERVAL_MS;

            if (isConfirmedScan) {
                if (event && event.preventDefault) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                restoreLeak();
                if (!isProcessingRef.current) {
                    isProcessingRef.current = true;
                    Promise.resolve(onBarcodeScanned(candidate))
                        .catch(error => console.error('Barcode handler error:', error))
                        .finally(() => { isProcessingRef.current = false; });
                }
            }

            resetBuffer();
        };

        const handleKeyDown = (e) => {
            if (!e || !e.key) {
                resetBuffer();
                return;
            }
            
            if (e.ctrlKey || e.metaKey || e.altKey) {
                resetBuffer();
                return;
            }
            if (e.key === 'Shift' || e.key === 'CapsLock') {
                return;
            }

            const now = Date.now();
            const delta = now - lastTimeRef.current;
            lastTimeRef.current = now;

            const target = e.target;
            const isEditable = !!target && !!target.tagName && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            );

            if (e.key === 'Enter' || e.key === 'Tab') {
                if (bufferRef.current) commitBufferedScan(e);
                return;
            }

            if (e.key.length !== 1) {
                resetBuffer();
                return;
            }

            if (bufferRef.current.length === 0 || delta > SCAN_MAX_INTERVAL_MS) {
                bufferRef.current = e.key;
                scanStartedAtRef.current = now;
                isLikelyScanningRef.current = false;
                leakSnapshotRef.current = isEditable && target ? {
                    element: target,
                    value: target.value || '',
                    selectionStart: target.selectionStart || 0
                } : null;
            } else {
                bufferRef.current += e.key;
                isLikelyScanningRef.current = true;
                if (isEditable && e.preventDefault) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }

            clearTimeout(scanTimerRef.current);
            scanTimerRef.current = setTimeout(() => commitBufferedScan(), BUFFER_TIMEOUT_MS);
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            clearTimeout(scanTimerRef.current);
        };
    }, [onBarcodeScanned, isActive]);
};

// ==================== INTEGER ONLY HELPER CALCULATIONS ====================
const calculateItemTotal = (item) => {
    const quantity = Math.round(parseFloat(item.quantity) || 0);
    const price = Math.round(parseFloat(item.price) || 0);
    return Math.round(price * quantity).toString();
};

const generateBatchNumber = (productName) => {
    if (!productName) return '';
    const prefix = productName.substring(0, 3).toUpperCase();
    return `${prefix}0001`;
};

const incrementBatchNumber = (batchNo) => {
    if (!batchNo) return '';
    const match = batchNo.match(/^([A-Za-z]+)(\d+)$/);
    if (match) {
        const prefix = match[1];
        const num = parseInt(match[2], 10) + 1;
        const paddedNum = String(num).padStart(match[2].length, '0');
        return `${prefix}${paddedNum}`;
    }
    return `${batchNo}0001`;
};

const calculateGrandTotal = (items) => {
    let total = 0;
    items.forEach(item => {
        const qty = Math.round(parseFloat(item.quantity) || 0);
        const price = Math.round(parseFloat(item.price) || 0);
        total += price * qty;
    });
    return Math.round(total);
};

// ==================== TOAST NOTIFICATION ====================
const ToastNotification = ({ toast, setToast }) => {
    if (!toast.show) return null;
    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{toast.type.toUpperCase()}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==================== ITEMS TABLE COMPONENT ====================
const ItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    disabled,
    user,
    shopId,
    showToast,
    allowEmpty = false,
    // Global search props
    globalSearchTerm = '',
    onGlobalSearchChange = () => {},
    filteredProducts = [],
    showGlobalDropdown = false,
    onSelectProduct = () => {},
    isSearching = false
}) => {
    const [products, setProducts] = useState([]);
    const [filteredProductsByIndex, setFilteredProductsByIndex] = useState({});
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdown, setShowProductDropdown] = useState({});
    const productSearchRefs = useRef({});
    const productSearchTimersRef = useRef({});
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadProducts = async () => {
        if (!user?.username || !shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_all_products', username: user?.username, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') setProducts(data.data || []);
        } catch (error) { console.error('Error loading products:', error); }
    };

    useEffect(() => { loadProducts(); }, [user?.username, shopId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event || !event.target) return;
            Object.keys(productSearchRefs.current).forEach(index => {
                if (productSearchRefs.current[index] && !productSearchRefs.current[index].contains(event.target)) {
                    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const handleProductSearch = (index, term) => {
        setProductSearchTerms(prev => ({ ...prev, [index]: term }));
        clearTimeout(productSearchTimersRef.current[index]);

        const item = items[index];
        if (term && term.trim() !== '' && item && (!item.product_id || item.product_id === '')) {
            if (!item.batch_no || item.batch_no.trim() === '') {
                const newBatch = generateBatchNumber(term.trim());
                onItemChange(index, 'batch_no', newBatch);
            }
        }

        if (term.trim() === '') {
            setFilteredProductsByIndex(prev => ({ ...prev, [index]: [] }));
            setShowProductDropdown(prev => ({ ...prev, [index]: false }));
        } else {
            productSearchTimersRef.current[index] = setTimeout(() => {
                const query = term.trim().toLowerCase();
                const filtered = products.filter(product =>
                    product.product_name?.toLowerCase().includes(query) ||
                    product.batch_no?.toLowerCase().includes(query) ||
                    product.barcode?.toLowerCase().includes(query) ||
                    product.product_code?.toLowerCase().includes(query)
                ).slice(0, 30);
                setFilteredProductsByIndex(prev => ({ ...prev, [index]: filtered }));
                setShowProductDropdown(prev => ({ ...prev, [index]: filtered.length > 0 }));
            }, 100);
        }
    };

    const selectProduct = (index, product) => {
        const costPriceInt = Math.round(parseFloat(product.cost_price || product.cost_per_pack || 0));
        const newItem = {
            product_name: product.product_name || '',
            batch_no: product.batch_no || generateBatchNumber(product.product_name),
            product_code: product.product_code || '',
            barcode: product.barcode || '',
            company: product.company || '',
            price: costPriceInt > 0 ? String(costPriceInt) : '0',
            quantity: '1',
            product_id: product.id || ''
        };
        onItemChange(index, 'batch_update', newItem);
        setProductSearchTerms(prev => ({ ...prev, [index]: product.product_name || '' }));
        setShowProductDropdown(prev => ({ ...prev, [index]: false }));
        if (showToast) showToast('success', `"${product.product_name}" selected`);
    };

    return (
        <div className="ozui-items-table-container" style={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}>
            <div className="ozui-table-header-actions" style={{ width: '100%', flexWrap: 'wrap', gap: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faBoxes} /> Purchase Items ({items.length})
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: '1 1 auto', justifyContent: 'flex-end' }}>
                    {/* Global Search Field - Above the table */}
                    <div style={{ position: 'relative', flex: '1 1 250px', minWidth: '180px', maxWidth: '400px' }}>
                        <input
                            type="text"
                            value={globalSearchTerm}
                            onChange={(e) => onGlobalSearchChange(e.target.value)}
                            onFocus={() => {
                                if (globalSearchTerm && globalSearchTerm.length >= 2) {
                                    // Trigger search on focus
                                }
                            }}
                            placeholder="Search products by name, barcode, batch..."
                            disabled={disabled}
                            autoComplete="off"
                            style={{
                                width: '100%',
                                padding: '8px 12px 8px 36px',
                                border: '1px solid var(--ozui-border)',
                                borderRadius: '8px',
                                fontSize: '0.85rem',
                                backgroundColor: 'var(--ozui-bg)',
                                color: 'var(--ozui-text)',
                                outline: 'none',
                                height: '38px',
                                boxSizing: 'border-box'
                            }}
                        />
                        <FontAwesomeIcon 
                            icon={faSearch} 
                            style={{ 
                                position: 'absolute', 
                                left: '10px', 
                                top: '50%', 
                                transform: 'translateY(-50%)',
                                color: 'var(--ozui-text-light)',
                                fontSize: '14px'
                            }} 
                        />
                        {isSearching && (
                            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                <FontAwesomeIcon icon={faSpinner} spin style={{ color: 'var(--ozui-text-light)' }} />
                            </span>
                        )}
                        {showGlobalDropdown && filteredProducts.length > 0 && (
                            <div style={{
                                position: 'absolute',
                                top: 'calc(100% + 2px)',
                                left: 0,
                                right: 0,
                                zIndex: 9999999,
                                backgroundColor: 'var(--ozui-bg)',
                                border: '1px solid var(--ozui-border)',
                                borderRadius: '8px',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                minWidth: '280px'
                            }}>
                                {filteredProducts.map(product => (
                                    <div
                                        key={product.id}
                                        onClick={() => onSelectProduct(product)}
                                        style={{
                                            padding: '10px 14px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid var(--ozui-border-light)',
                                            transition: 'background-color 0.15s ease',
                                            backgroundColor: 'var(--ozui-bg)',
                                            color: 'var(--ozui-text)'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--ozui-surface)'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--ozui-bg)'}
                                    >
                                        <div style={{
                                            fontWeight: '600',
                                            fontSize: '0.88rem',
                                            color: 'var(--ozui-text)',
                                            marginBottom: '4px',
                                            wordBreak: 'break-word'
                                        }}>
                                            {product.product_name}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            gap: '12px',
                                            fontSize: '0.72rem',
                                            color: 'var(--ozui-text-light)',
                                            flexWrap: 'wrap',
                                            marginBottom: '4px'
                                        }}>
                                            {product.batch_no && (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <FontAwesomeIcon icon={faTag} style={{ fontSize: '10px' }} />
                                                    {product.batch_no}
                                                </span>
                                            )}
                                            {product.barcode && (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <FontAwesomeIcon icon={faBarcode} style={{ fontSize: '10px' }} />
                                                    {product.barcode}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            gap: '16px',
                                            fontSize: '0.72rem',
                                            flexWrap: 'wrap'
                                        }}>
                                            <span style={{
                                                color: '#059669',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <FontAwesomeIcon icon={faRupeeSign} style={{ fontSize: '10px' }} />
                                                {' '}
                                                {Math.round(parseFloat(product.cost_price || product.cost_per_pack || 0)).toFixed(2)}
                                                {' '}
                                                <span style={{ fontWeight: '400', color: 'var(--ozui-text-light)' }}>
                                                    /pack
                                                </span>
                                            </span>
                                            <span style={{
                                                color: 'var(--ozui-text-light)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                Stock: {product.quantity || 0}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button type="button" className="ozui-btn-add-item" onClick={onAdd} disabled={disabled}>
                        <FontAwesomeIcon icon={faPlus} /><span>Add Item</span>
                    </button>
                </div>
            </div>

            {/* DESKTOP COMPACT DATA TABLE VIEW */}
            <div className="ozui-table-wrapper ozui-desktop-only" style={{ overflowX: 'auto' }}>
                <table className="ozui-items-table" style={{ minWidth: '900px' }}>
                    <thead>
                        <tr>
                            <th>Sr#</th>
                            <th>Batch <span className="ozui-required">*</span></th>
                            <th>Product Name <span className="ozui-required">*</span></th>
                            <th>Company</th>
                            <th>Cost Price <span className="ozui-required">*</span></th>
                            <th>Qty <span className="ozui-required">*</span></th>
                            <th>Total</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const filteredForIndex = filteredProductsByIndex[index] || [];
                            const showDrop = showProductDropdown[index] || false;
                            const searchTerm = productSearchTerms[index] !== undefined ? productSearchTerms[index] : item.product_name;

                            return (
                                <tr key={index}>
                                    <td><strong>{index + 1}</strong></td>
                                    <td>
                                        <div className="ozui-input-wrapper ozui-table-batch-wrapper">
                                            <input
                                                type="text"
                                                id={`dt_batch_${index}`}
                                                value={item.batch_no || ''}
                                                readOnly
                                                disabled={disabled}
                                                placeholder=" "
                                            />
                                            <label htmlFor={`dt_batch_${index}`}><FontAwesomeIcon icon={faTag} /> Batch</label>
                                            {item.product_name && (
                                                <button
                                                    type="button"
                                                    onClick={() => onItemChange(index, 'batch_no', incrementBatchNumber(item.batch_no))}
                                                    className="ozui-batch-btn"
                                                    title="Increment Batch"
                                                >
                                                    <FontAwesomeIcon icon={faPlus} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="text"
                                                id={`dt_product_${index}`}
                                                value={searchTerm}
                                                onChange={e => {
                                                    onItemChange(index, 'product_name', e.target.value);
                                                    handleProductSearch(index, e.target.value);
                                                }}
                                                onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                                                placeholder=" "
                                                disabled={disabled}
                                                autoComplete="off"
                                            />
                                            <label htmlFor={`dt_product_${index}`}><FontAwesomeIcon icon={faBox} /> Product Name</label>
                                            {showDrop && filteredForIndex.length > 0 && (
                                                <div className="ozui-product-dropdown-table">
                                                    {filteredForIndex.map(p => (
                                                        <div key={p.id} className="ozui-product-option" onClick={() => selectProduct(index, p)}>
                                                            <div className="ozui-product-name">{p.product_name}</div>
                                                            <div className="ozui-product-details">Batch: {p.batch_no} | Rs. {Math.round(p.cost_price || 0)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="text"
                                                id={`dt_company_${index}`}
                                                value={item.company || ''}
                                                onChange={e => onItemChange(index, 'company', e.target.value)}
                                                placeholder=" "
                                                disabled={disabled}
                                            />
                                            <label htmlFor={`dt_company_${index}`}><FontAwesomeIcon icon={faBuilding} /> Company</label>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="number"
                                                id={`dt_price_${index}`}
                                                value={item.price || ''}
                                                onChange={e => onItemChange(index, 'price', e.target.value)}
                                                placeholder=" "
                                                step="1"
                                                disabled={disabled}
                                            />
                                            <label htmlFor={`dt_price_${index}`}><FontAwesomeIcon icon={faRupeeSign} /> Cost Price</label>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="number"
                                                id={`dt_qty_${index}`}
                                                value={item.quantity || ''}
                                                onChange={e => onItemChange(index, 'quantity', e.target.value)}
                                                placeholder=" "
                                                step="1"
                                                disabled={disabled}
                                            />
                                            <label htmlFor={`dt_qty_${index}`}><FontAwesomeIcon icon={faBoxes} /> Qty</label>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="text"
                                                id={`dt_total_${index}`}
                                                value={calculateItemTotal(item)}
                                                readOnly
                                                disabled
                                                placeholder=" "
                                                className="ozui-readonly-input ozui-total-display"
                                            />
                                            <label htmlFor={`dt_total_${index}`}><FontAwesomeIcon icon={faCalculator} /> Total</label>
                                        </div>
                                    </td>
                                    <td>
                                        <button type="button" className="ozui-table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || (!allowEmpty && items.length === 1)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* MOBILE TOUCH CARD VIEW */}
            <div className="ozui-mobile-items-list ozui-mobile-only">
                {items.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--ozui-text-light)' }}>
                        No items added yet. Search and add products above.
                    </div>
                ) : (
                    items.map((item, index) => {
                        const filteredForIndex = filteredProductsByIndex[index] || [];
                        const showDrop = showProductDropdown[index] || false;
                        const searchTerm = productSearchTerms[index] !== undefined ? productSearchTerms[index] : item.product_name;

                        return (
                            <div key={index} className="ozui-mobile-item-card">
                                <div className="ozui-mobile-card-header">
                                    <span className="ozui-mobile-sr">#{index + 1}</span>
                                    <span className="ozui-mobile-total">Total: Rs. {calculateItemTotal(item)}</span>
                                    <button
                                        type="button"
                                        className="ozui-mobile-remove-btn"
                                        onClick={() => onRemove(index)}
                                        disabled={disabled || (!allowEmpty && items.length === 1)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>

                                <div className="ozui-mobile-card-body">
                                    <div className="ozui-field" ref={el => productSearchRefs.current[`mob_${index}`] = el}>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="text"
                                                id={`mob_prod_${index}`}
                                                value={searchTerm}
                                                onChange={e => {
                                                    onItemChange(index, 'product_name', e.target.value);
                                                    handleProductSearch(index, e.target.value);
                                                }}
                                                onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                                                placeholder=" "
                                                disabled={disabled}
                                                autoComplete="off"
                                            />
                                            <label htmlFor={`mob_prod_${index}`}><FontAwesomeIcon icon={faBox} /> Product Name <span>*</span></label>
                                            {showDrop && filteredForIndex.length > 0 && (
                                                <div className="ozui-product-dropdown-table">
                                                    {filteredForIndex.map(p => (
                                                        <div key={p.id} className="ozui-product-option" onClick={() => selectProduct(index, p)}>
                                                            <div className="ozui-product-name">{p.product_name}</div>
                                                            <div className="ozui-product-details">Batch: {p.batch_no} | Rs. {Math.round(p.cost_price || 0)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="ozui-mobile-grid-2">
                                        <div className="ozui-field">
                                            <div className="ozui-input-wrapper ozui-table-batch-wrapper">
                                                <input type="text" id={`mob_batch_${index}`} value={item.batch_no || ''} readOnly disabled placeholder=" " />
                                                <label htmlFor={`mob_batch_${index}`}><FontAwesomeIcon icon={faTag} /> Batch <span>*</span></label>
                                                {item.product_name && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onItemChange(index, 'batch_no', incrementBatchNumber(item.batch_no))}
                                                        className="ozui-batch-btn"
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="ozui-field">
                                            <div className="ozui-input-wrapper">
                                                <input
                                                    type="text"
                                                    id={`mob_comp_${index}`}
                                                    value={item.company || ''}
                                                    onChange={e => onItemChange(index, 'company', e.target.value)}
                                                    placeholder=" "
                                                    disabled={disabled}
                                                />
                                                <label htmlFor={`mob_comp_${index}`}><FontAwesomeIcon icon={faBuilding} /> Company</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="ozui-mobile-grid-2">
                                        <div className="ozui-field">
                                            <div className="ozui-input-wrapper">
                                                <input
                                                    type="number"
                                                    id={`mob_price_${index}`}
                                                    value={item.price || ''}
                                                    onChange={e => onItemChange(index, 'price', e.target.value)}
                                                    placeholder=" "
                                                    step="1"
                                                    disabled={disabled}
                                                />
                                                <label htmlFor={`mob_price_${index}`}><FontAwesomeIcon icon={faRupeeSign} /> Cost Price <span>*</span></label>
                                            </div>
                                        </div>

                                        <div className="ozui-field">
                                            <div className="ozui-input-wrapper">
                                                <input
                                                    type="number"
                                                    id={`mob_qty_${index}`}
                                                    value={item.quantity || ''}
                                                    onChange={e => onItemChange(index, 'quantity', e.target.value)}
                                                    placeholder=" "
                                                    step="1"
                                                    disabled={disabled}
                                                />
                                                <label htmlFor={`mob_qty_${index}`}><FontAwesomeIcon icon={faBoxes} /> Qty <span>*</span></label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

// ==================== MAIN PURCHASES COMPONENT ====================
const ElectronicsPurchases = ({ purchaseId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const initialFormData = {
        date: new Date().toISOString().split('T')[0],
        bill_no: '',
        supplierid: '',
        supplier_name: '',
        phone: '',
        address: '',
        bill_file: null,
        grand_total: '0',
        paid: '0',
        balance: '0',
        items: []
    };

    const [formData, setFormData] = useState(initialFormData);
    const [suppliers, setSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });

    const purchasePayment = usePurchasePayments({
        apiUrl: ACCOUNTS_ENDPOINT,
        shopId: currentShop?.shop_id,
        username: user?.username,
        supplierId: formData.supplierid,
        purchaseId,
        invoiceTotal: Number(formData.grand_total) || 0
    });

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            paid: purchasePayment.currentInvoicePaid.toFixed(2),
            balance: purchasePayment.currentInvoiceBalance.toFixed(2)
        }));
    }, [purchasePayment.currentInvoicePaid, purchasePayment.currentInvoiceBalance]);

    // Global Search States
    const [globalSearchTerm, setGlobalSearchTerm] = useState('');
    const [filteredGlobalProducts, setFilteredGlobalProducts] = useState([]);
    const [showGlobalDropdown, setShowGlobalDropdown] = useState(false);
    const [searchingGlobalProducts, setSearchingGlobalProducts] = useState(false);

    // Drafts States
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    const draftsDropdownRef = useRef(null);

    const [isScannerActive, setIsScannerActive] = useState(true);
    const toastTimerRef = useRef(null);
    const supplierSearchRef = useRef(null);
    const isSavingRef = useRef(false);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => setToast({ show: false, type: '', message: '' }), 3000);
    }, []);

    // Dark Mode Sync
    useEffect(() => {
        const sync = () => {
            const dark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(dark);
            document.body.classList.toggle('ozui-dark', dark);
            document.documentElement.classList.toggle('ozui-dark', dark);
        };
        sync();
        window.addEventListener('storage', sync);
        window.addEventListener('darkModeToggled', sync);
        return () => {
            window.removeEventListener('storage', sync);
            window.removeEventListener('darkModeToggled', sync);
        };
    }, []);

    // OUTSIDE CLICK & TOUCH DISMISSAL FOR SUPPLIER & DRAFTS DROPDOWNS
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e || !e.target) return;
            if (supplierSearchRef.current && !supplierSearchRef.current.contains(e.target)) {
                setShowSupplierDropdown(false);
            }
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(e.target)) {
                setShowDraftsList(false);
            }
        };

        const handleKeyDown = (e) => {
            if (e && e.key === 'Escape') {
                setShowSupplierDropdown(false);
                setShowDraftsList(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Drafts Manager
    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `purchase_draft_${userId}_${shopId}`;
    };

    const loadDrafts = useCallback(() => {
        try {
            const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
            setSavedDrafts(currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts);
        } catch (error) {
            console.error('Error loading drafts:', error);
            setSavedDrafts([]);
        }
    }, [currentShop, user]);

    useEffect(() => { loadDrafts(); }, [loadDrafts]);

    const saveDraft = () => {
        const hasData = formData.bill_no?.trim() || formData.supplierid || formData.items?.some(i => i.product_name?.trim());
        if (!hasData) { showToast('warning', 'Cannot save empty draft'); return; }

        const draft = {
            id: Date.now(),
            ...formData,
            paymentLines: purchasePayment.payments,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id
        };
        const existing = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        existing.unshift(draft);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(existing.slice(0, 20)));
        showToast('success', 'Draft saved!');
        loadDrafts();
    };

    const loadDraft = (draft) => {
        setFormData({ ...initialFormData, ...draft, date: draft.date || new Date().toISOString().split('T')[0], items: draft.items || [] });
        purchasePayment.setPayments(Array.isArray(draft.paymentLines) && draft.paymentLines.length
            ? draft.paymentLines
            : [{ payment_type: 'cash', account_id: '', amount: '', reference_no: '' }]);
        if (draft.supplier_name) setSearchTerm(draft.supplier_name);
        setShowDraftsList(false);
        showToast('success', 'Draft loaded!');
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(drafts.filter(d => d.id !== draftId)));
        loadDrafts();
        showToast('success', 'Draft deleted!');
    };

    const clearDraft = () => {
        setFormData(initialFormData);
        purchasePayment.resetPayments();
        setSearchTerm('');
        setGlobalSearchTerm('');
        setFilteredGlobalProducts([]);
        setShowGlobalDropdown(false);
        showToast('success', 'Form cleared');
    };

    // Load Suppliers
    const loadSuppliers = async () => {
        if (!user?.username || !currentShop?.shop_id) return;
        try {
            const r = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_suppliers', searchTerm: '', username: user.username, shop_id: currentShop.shop_id })
            });
            const d = await r.json();
            if (d.status === 'success' && d.data) { 
                setSuppliers(d.data); 
                setFilteredSuppliers(d.data); 
            }
        } catch (e) { console.error('Error loading suppliers:', e); }
    };

    useEffect(() => { loadSuppliers(); }, [user?.username, currentShop?.shop_id]);

    // ==================== SEARCH GLOBAL PRODUCTS ====================
    const searchGlobalProducts = useCallback(async (term) => {
        if (!term || term.length < 2 || !user?.username || !currentShop?.shop_id) {
            setFilteredGlobalProducts([]);
            setShowGlobalDropdown(false);
            return;
        }
        setSearchingGlobalProducts(true);
        try {
            const r = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'search_products', 
                    searchTerm: term, 
                    username: user.username, 
                    shop_id: currentShop.shop_id 
                })
            });
            const d = await r.json();
            if (d.status === 'success') {
                const products = (d.data || []).map(p => ({
                    id: p.id,
                    product_name: p.product_name || '',
                    product_code: p.product_code || '',
                    batch_no: p.batch_no || '',
                    barcode: p.barcode || '',
                    cost_price: parseFloat(p.cost_price) || 0,
                    cost_per_pack: parseFloat(p.cost_per_pack) || 0,
                    quantity: parseFloat(p.quantity) || 0,
                    company: p.company || '',
                    category: p.category || ''
                }));
                setFilteredGlobalProducts(products);
                setShowGlobalDropdown(products.length > 0);
            } else {
                setFilteredGlobalProducts([]);
                setShowGlobalDropdown(false);
            }
        } catch (e) {
            console.error('Product search error:', e);
            setFilteredGlobalProducts([]);
            setShowGlobalDropdown(false);
        } finally {
            setSearchingGlobalProducts(false);
        }
    }, [user?.username, currentShop?.shop_id]);

    // ==================== SELECT PRODUCT FROM SEARCH ====================
    const handleSelectProduct = useCallback((product) => {
        const costPriceInt = Math.round(parseFloat(product.cost_price || product.cost_per_pack || 0));
        const newItem = {
            product_name: product.product_name || '',
            batch_no: product.batch_no || generateBatchNumber(product.product_name),
            product_code: product.product_code || '',
            barcode: product.barcode || '',
            company: product.company || '',
            price: costPriceInt > 0 ? String(costPriceInt) : '0',
            quantity: '1',
            product_id: product.id || ''
        };
        
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));
        
        setGlobalSearchTerm('');
        setShowGlobalDropdown(false);
        showToast('success', `"${product.product_name}" added to purchase`);
    }, [showToast]);

    const handleSupplierSearch = (searchValue) => {
        setSearchTerm(searchValue);
        setShowSupplierDropdown(true);
        
        if (!searchValue.trim()) {
            setFilteredSuppliers(suppliers);
            return;
        }
        
        const filtered = suppliers.filter(s => 
            s.supplier_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
            s.phone?.toLowerCase().includes(searchValue.toLowerCase())
        );
        setFilteredSuppliers(filtered);
    };

    const selectSupplier = (supplier) => {
        setFormData(prev => ({
            ...prev,
            supplierid: supplier.supplierid,
            supplier_name: supplier.supplier_name || '',
            phone: supplier.phone || '',
            address: supplier.address || ''
        }));
        setSearchTerm(supplier.supplier_name || '');
        setShowSupplierDropdown(false);
    };

    // Auto-calculate Integer Totals
    useEffect(() => {
        const grandTotal = calculateGrandTotal(formData.items);
        setFormData(prev => ({
            ...prev,
            grand_total: String(grandTotal)
        }));
    }, [formData.items]);

    const handleItemChange = (index, field, value) => {
        if (field === 'batch_update') {
            setFormData(prev => {
                const updatedItems = [...prev.items];
                updatedItems[index] = { ...updatedItems[index], ...value };
                return { ...prev, items: updatedItems };
            });
        } else {
            setFormData(prev => {
                const updatedItems = [...prev.items];
                updatedItems[index] = { ...updatedItems[index], [field]: value };
                return { ...prev, items: updatedItems };
            });
        }
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { product_name: '', batch_no: '', price: '', quantity: '1', company: '' }]
        }));
    };

    const removeItem = (index) => {
        setFormData(prev => {
            const updated = [...prev.items];
            updated.splice(index, 1);
            return { ...prev, items: updated };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSavingRef.current) return;
        
        if (!formData.bill_no) { showToast('error', 'Bill Number is required'); return; }
        if (!formData.supplierid) { showToast('error', 'Please select a supplier'); return; }
        if (formData.items.length === 0) { showToast('error', 'Add at least one item'); return; }
        
        const invalidItems = formData.items.filter(item => 
            !item.product_name || !item.price || !item.quantity || !item.batch_no
        );
        if (invalidItems.length > 0) { 
            showToast('error', 'Please fill all required fields for items'); 
            return; 
        }

        const paymentError = purchasePayment.validate();
        if (paymentError) {
            showToast('error', paymentError);
            return;
        }

        isSavingRef.current = true;
        setSaving(true);

        try {
            const purchaseData = {
                action: isEditMode ? 'edit' : 'add',
                username: user?.username,
                shop_id: currentShop?.shop_id,
                id: purchaseId || undefined,
                date: formData.date,
                bill_no: formData.bill_no,
                supplierid: formData.supplierid,
                supplier_name: formData.supplier_name,
                phone: formData.phone || '',
                address: formData.address || '',
                grand_total: formData.grand_total,
                paid: purchasePayment.currentInvoicePaid,
                balance: purchasePayment.currentInvoiceBalance,
                ...purchasePayment.payload,
                items: formData.items.map(item => ({
                    product_name: item.product_name,
                    batch_no: item.batch_no,
                    company: item.company || '',
                    cost_per_pack: item.price,
                    price: item.price,
                    quantity: item.quantity,
                    total: calculateItemTotal(item),
                    product_id: item.product_id || null,
                    barcode: item.barcode || null
                }))
            };

            console.log('Sending purchase data:', purchaseData);

            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(purchaseData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Server response:', result);

            if (result.status === 'success') {
                showToast('success', result.message || 'Purchase saved successfully!');
                clearDraft();
                if (onSuccess) {
                    onSuccess(result);
                }
            } else if (result.already_exists) {
                showToast('warning', `Purchase with bill #${formData.bill_no} already exists`);
            } else {
                throw new Error(result.message || 'Failed to save purchase');
            }
        } catch (err) {
            console.error('Save error:', err);
            showToast('error', `Error saving purchase: ${err.message}`);
        } finally {
            isSavingRef.current = false;
            setSaving(false);
        }
    };

    // Barcode scanner handler
    const handleBarcodeScanned = useCallback(async (barcode) => {
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'get_product_by_barcode', 
                    barcode: barcode,
                    username: user?.username, 
                    shop_id: currentShop?.shop_id 
                })
            });
            const data = await response.json();
            
            if (data.status === 'success' && data.data) {
                const product = data.data;
                const newItem = {
                    product_name: product.product_name || '',
                    batch_no: product.batch_no || generateBatchNumber(product.product_name),
                    product_code: product.product_code || '',
                    barcode: product.barcode || '',
                    company: product.company || '',
                    price: Math.round(parseFloat(product.cost_price || product.cost_per_pack || 0)).toString(),
                    quantity: '1',
                    product_id: product.id || ''
                };
                
                setFormData(prev => ({
                    ...prev,
                    items: [...prev.items, newItem]
                }));
                showToast('success', `Product "${product.product_name}" added via barcode`);
            } else {
                showToast('error', 'Product not found for barcode: ' + barcode);
            }
        } catch (error) {
            console.error('Barcode lookup error:', error);
            showToast('error', 'Error looking up barcode');
        }
    }, [user?.username, currentShop?.shop_id, showToast]);

    // Initialize barcode scanner
    useBarcodeScanner(handleBarcodeScanned, isScannerActive);

    // Load purchase data for edit mode
    useEffect(() => {
        if (purchaseId && isEditMode) {
            const loadPurchase = async () => {
                try {
                    const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                            action: 'get_record',
                            id: purchaseId,
                            username: user?.username, 
                            shop_id: currentShop?.shop_id 
                        })
                    });
                    const data = await response.json();
                    
                    if (data.status === 'success' && data.data) {
                        const purchase = data.data;
                        purchasePayment.loadSavedPurchase(purchase);
                        setFormData({
                            ...initialFormData,
                            date: purchase.date || initialFormData.date,
                            bill_no: purchase.bill_no || '',
                            supplierid: purchase.supplierid || '',
                            supplier_name: purchase.supplier_name || '',
                            phone: purchase.phone || '',
                            address: purchase.address || '',
                            grand_total: purchase.grand_total || '0',
                            paid: purchase.paid || '0',
                            balance: purchase.balance || '0',
                            items: (purchase.items || []).map(item => ({
                                product_name: item.product_name || '',
                                batch_no: item.batch_no || '',
                                company: item.company || '',
                                price: item.cost_per_pack || item.price || '',
                                quantity: item.quantity || '1',
                                product_id: item.product_id || null,
                                barcode: item.barcode || null
                            }))
                        });
                        if (purchase.supplier_name) setSearchTerm(purchase.supplier_name);
                    } else {
                        showToast('error', data.message || 'Purchase not found');
                    }
                } catch (error) {
                    console.error('Error loading purchase:', error);
                    showToast('error', 'Error loading purchase data');
                }
            };
            loadPurchase();
        }
    }, [purchaseId, isEditMode, user?.username, currentShop?.shop_id]);

    return (
        <div className={`ozui-container ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} />

            {/* HEADER WITH ACTION BUTTONS & DRAFTS */}
            <div className="ozui-sticky-header">
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode ? 'Edit Purchase' : 'New Purchase'}</h1>
                            <p>Enter purchase bill details & items</p>
                        </div>
                    </div>
                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button className="ozui-action-btn clear-draft" onClick={clearDraft} title="Clear Form">
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>Clear</span>
                            </button>

                            <button className="ozui-action-btn save-draft" onClick={saveDraft} title="Save Draft">
                                <FontAwesomeIcon icon={faCopy} />
                                <span>Save Draft</span>
                            </button>

                            {/* DRAFTS DROPDOWN */}
                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button
                                    className="ozui-action-btn drafts-btn"
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title="View Drafts"
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>Drafts</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>Saved Drafts</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">No drafts saved</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">{draft.bill_no || `Draft #${draft.id}`}</div>
                                                            <div className="draft-details">{draft.supplier_name}</div>
                                                        </div>
                                                        <button className="delete-draft-btn" onClick={(e) => { e.stopPropagation(); deleteDraft(draft.id); }}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button className="ozui-action-btn scanner-toggle" onClick={() => setIsScannerActive(!isScannerActive)}>
                                <FontAwesomeIcon icon={isScannerActive ? faToggleOn : faToggleOff} />
                                <span>{isScannerActive ? 'Scanner ON' : 'Scanner OFF'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="date"
                                id="purchase_date"
                                value={formData.date}
                                onChange={e => setFormData({ ...formData, date: e.target.value })}
                                placeholder=" "
                            />
                            <label htmlFor="purchase_date"><FontAwesomeIcon icon={faCalendarAlt} /> Date <span>*</span></label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="bill_no"
                                id="purchase_bill_no"
                                value={formData.bill_no}
                                onChange={e => setFormData({ ...formData, bill_no: e.target.value })}
                                placeholder=" "
                            />
                            <label htmlFor="purchase_bill_no"><FontAwesomeIcon icon={faFileInvoice} /> Bill Number <span>*</span></label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field" ref={supplierSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="supplier_search"
                                value={searchTerm}
                                onChange={e => handleSupplierSearch(e.target.value)}
                                onFocus={() => setShowSupplierDropdown(true)}
                                placeholder=" "
                                autoComplete="off"
                            />
                            <label htmlFor="supplier_search"><FontAwesomeIcon icon={faUser} /> Supplier <span>*</span></label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {showSupplierDropdown && filteredSuppliers.length > 0 && (
                            <div className="ozui-supplier-dropdown">
                                {filteredSuppliers.map(s => (
                                    <div key={s.supplierid} className="ozui-dropdown-item" onClick={() => selectSupplier(s)}>
                                        <div className="ozui-supplier-name">{s.supplier_name}</div>
                                        {s.phone && <div className="ozui-supplier-phone"><FontAwesomeIcon icon={faPhone} /> {s.phone}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="phone"
                                id="supplier_phone"
                                value={formData.phone}
                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                placeholder=" "
                            />
                            <label htmlFor="supplier_phone"><FontAwesomeIcon icon={faPhone} /> Phone</label>
                        </div>
                    </div>
                </div>

                {/* ITEMS SECTION with Global Search */}
                <ItemsTable
                    items={formData.items}
                    onItemChange={handleItemChange}
                    onRemove={removeItem}
                    onAdd={addItem}
                    errors={errors}
                    disabled={saving}
                    user={user}
                    shopId={currentShop?.shop_id}
                    showToast={showToast}
                    globalSearchTerm={globalSearchTerm}
                    onGlobalSearchChange={(term) => {
                        setGlobalSearchTerm(term);
                        searchGlobalProducts(term);
                    }}
                    filteredProducts={filteredGlobalProducts}
                    showGlobalDropdown={showGlobalDropdown}
                    onSelectProduct={handleSelectProduct}
                    isSearching={searchingGlobalProducts}
                />

                {formData.items.length > 0 && (
                    <div className="ozui-form-section">
                        <SalePaymentSection
                            payment={purchasePayment}
                            direction="out"
                            title="Supplier Payment Details"
                            helpText="Pay from one account or split the amount across multiple methods."
                            subtotal={Number(formData.grand_total) || 0}
                            previousBalance={purchasePayment.previousBalance}
                            creditBalance={purchasePayment.creditBalance}
                            payableTotal={purchasePayment.payableTotal}
                            paidTotal={purchasePayment.paidTotal}
                            balanceAfter={purchasePayment.balanceAfter}
                            showDiscount={false}
                            referenceEnabled
                            disabled={saving}
                            translations={{
                                paymentMethod: 'Payment Methods',
                                previousBalance: 'Previous Supplier Balance',
                                subtotal: 'Current Purchase Total',
                                grandTotal: 'Total Payable',
                                paidAmount: 'Total Paid Now',
                                balance: 'Supplier Balance After'
                            }}
                        />
                    </div>
                )}

                <div className="ozui-form-actions">
                    <button type="submit" className="ozui-btn ozui-btn-primary" disabled={saving || formData.items.length === 0}>
                        {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
                        <span>{isEditMode ? 'Update Purchase' : 'Save Purchase'}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ElectronicsPurchases;
export { ItemsTable as PurchaseItemsTable, useBarcodeScanner };
